import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import Avatar from '../ui/Avatar';
import useAuthStore from '../../stores/authStore';
import useProjectStore from '../../stores/projectStore';
import useLayoutStore from '../../stores/layoutStore';
import { useSettings } from '../../hooks/useSettings';
import { useProjectIntegrations } from '../../hooks/useProjectIntegrations';

// ─── Icon Paths (Lucide-Style, inline für minimale Deps) ───────────────────
const I = {
  start:      '<path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2h-4v-7H9v7H5a2 2 0 0 1-2-2z"/>',
  projekt:    '<path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/>',
  funktionen: '<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>',
  agentCenter:'<rect x="4" y="6" width="16" height="12" rx="2"/><path d="M9 2v4"/><path d="M15 2v4"/><path d="M9 22v-4"/><path d="M15 22v-4"/><path d="M2 12h2"/><path d="M2 8h2"/><path d="M2 16h2"/><path d="M20 12h2"/><path d="M20 8h2"/><path d="M20 16h2"/><circle cx="10" cy="12" r="1.3"/><circle cx="14" cy="12" r="1.3"/>',
  agentur:    '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  wissen:     '<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2zM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>',
  system:     '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
  chevrons:   '<path d="m6 17 5-5-5-5"/><path d="m13 17 5-5-5-5"/>',
  logout:     '<path d="m16 17 5-5-5-5"/><path d="M21 12H9"/><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>',
  external:   '<path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>',
  menu:       '<line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="18" y2="18"/>',
  // Sub-Items
  overview:   '<rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/>',
  analyse:    '<line x1="12" x2="12" y1="20" y2="10"/><line x1="18" x2="18" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="16"/>',
  cluster:    '<circle cx="12" cy="5" r="2"/><circle cx="5" cy="19" r="2"/><circle cx="19" cy="19" r="2"/><path d="M7 17 11 7"/><path d="m13 7 4 10"/>',
  measures:   '<path d="m3 17 2 2 4-4"/><path d="m3 7 2 2 4-4"/><path d="M13 6h8"/><path d="M13 12h8"/><path d="M13 18h8"/>',
  seo:        '<path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/>',
  analytics:  '<path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/>',
  sea:        '<path d="m3 11 18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>',
  reports:    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/>',
  cockpit:    '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
  link:       '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
  kontakte:   '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>',
  kunden:     '<path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><rect width="10" height="10" x="7" y="7" rx="1"/>',
  knowledge:  '<path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/>',
  factors:    '<rect width="18" height="18" x="3" y="3" rx="2"/><path d="M9 3v18"/><path d="M15 3v18"/><path d="M3 9h18"/><path d="M3 15h18"/>',
  users:      '<path d="M18 21a8 8 0 0 0-16 0"/><circle cx="10" cy="8" r="5"/><path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3"/>',
  bookmarklet:'<path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/>',
  tracking:   '<path d="M2 12h6"/><path d="M22 12h-6"/><path d="M12 2v6"/><path d="M12 22v-6"/><circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="8"/>',
  agentOverview:'<circle cx="12" cy="12" r="9"/><path d="M12 3a9 9 0 0 1 9 9"/><path d="m9 12 2 2 4-4"/>',
  agentGrid:  '<rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/>',
  plus:       '<path d="M5 12h14"/><path d="M12 5v14"/>',
  cro:        '<path d="m3 17 6-6 4 4 8-8"/><path d="M14 7h7v7"/>',
  settings:   '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
};

function Icon({ path, className = 'w-[18px] h-[18px]' }) {
  return (
    <svg
      className={`${className} shrink-0`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: path }}
    />
  );
}

// ─── App-Settings Hook (mit live-Update via custom event) ───────────────────
function useAppSettings() {
  const { data: apiSettings } = useSettings();
  const [localOverride, setLocalOverride] = useState({});

  useEffect(() => {
    const handler = (e) => setLocalOverride(e.detail || {});
    window.addEventListener('settings-updated', handler);
    return () => window.removeEventListener('settings-updated', handler);
  }, []);

  return { ...(apiSettings || {}), ...localOverride };
}

// ─── Navigations-Definition ─────────────────────────────────────────────────
// `requires` (optional): Item nur zeigen, wenn mind. eine Integration gebucht
// Projekt-Navigation als Baum: Items mit optionalem `children` (aufklappbare Gruppe).
const PROJECT_NAV = [
  { to: '/projekt-uebersicht', icon: I.overview,    label: 'Übersicht' },
  { to: '/agenten',            icon: I.agentCenter, label: 'Agenten' },
  { to: '/analytics/ga4',      icon: I.analytics,   label: 'Analytics' },
  { label: 'SEO', icon: I.seo, children: [
    { to: '/seo/rankings',   icon: I.seo,      label: 'Rankings' },
    { to: '/seo/checkliste', icon: I.measures, label: 'Checkliste' },
    { to: '/seo/measures',   icon: I.cro,      label: 'Maßnahmen' },
  ] },
  { label: 'Conversion', icon: I.cro, children: [
    { to: '/cro/analyses', icon: I.analyse,  label: 'Conversion Analyse' },
    { to: '/cro/measures', icon: I.measures, label: 'CRO-Maßnahmen' },
  ] },
  { label: 'Reports', icon: I.reports, children: [
    { to: '/report',          icon: I.reports, label: 'Gesamtreport' },
    { to: '/project-reports', icon: I.reports, label: 'Monatsreports' },
    { to: '/cro/clusters',    icon: I.cluster, label: 'Analyse Cluster' },
    { to: '/seo/weekly',      icon: I.cockpit, label: 'Weekly' },
  ] },
];
const PROJECT_INTEGRATIONS_ITEM = { to: '/integrations', icon: I.link, label: 'Anbindungen' };

// Funktionen: tätigkeitsorientierte Tools. SEO ist als einziges OHNE Projektbezug nutzbar
// (Sistrix-Stil Suchschlitz). Die anderen verlinken auf die bestehenden Projekt-Ansichten.
const FUNKTIONEN_ITEMS = [
  { to: '/seo/check',          icon: I.seo,       label: 'SEO' },
  { to: '/sea/campaigns',      icon: I.sea,       label: 'SEA' },
  { to: '/cro/analyses',       icon: I.cro,       label: 'CRO' },
  { to: '/analytics/tracking', icon: I.tracking,  label: 'Tracking' },
];

// Agent Center: pro Projekt ein Überagent + Sub-Agents je Tätigkeit.
// Sub-Agents werden später an Kundenprojekte gebunden.
// Library: globale Vorlagen. Projekt-Agents: pro Projekt 1 Überagent + 5 Sub-Slots
// (SEO/SEA/CRO/Tracking/Reporting), Slot aktiv nur wenn der Kunde die Tätigkeit
// gebucht hat. Die festen "SEO-Agent / SEA-Agent / …" Sub-Items aus der Sidebar
// sind raus — die gehören in den Projekt-Agents-Screen, nicht in die Nav.
const AGENT_CENTER_ITEMS = [
  { to: '/agent-center/library', icon: I.agentOverview, label: 'Library' },
  { to: '/agent-center/agents',  icon: I.agentGrid,     label: 'Agent Library' },
  { to: '/agent-center',         icon: I.agentOverview, label: 'Pipelines', end: true },
];

const AGENTUR_ITEMS = [
  { to: '/onboarding', icon: I.plus,    label: 'Neuer Kunde' },
  { to: '/projects', icon: I.kunden,   label: 'Kunden & Projekte' },
  { to: '/contacts', icon: I.kontakte, label: 'Kontakte' },
];

const WISSEN_ITEMS = [
  { to: '/knowledge', icon: I.knowledge, label: 'Knowledge Base' },
  { to: '/factors',   icon: I.factors,   label: 'CRO-Faktoren' },
];

const SYSTEM_ITEMS = [
  { to: '/systemstatus', icon: I.system,      label: 'Systemstatus' },
  { to: '/users',       icon: I.users,        label: 'Benutzer & Tokens' },
  { to: '/bookmarklet', icon: I.bookmarklet,  label: 'Bookmarklet' },
  { to: '/settings',    icon: I.settings,     label: 'Einstellungen' },
];

// Route → Kategorie (für initiale aktive Markierung im Rail)
const ROUTE_CATEGORY = [
  { match: /^\/agent-center/, cat: 'agent' },
  { match: /^\/(cro|seo|sea|analytics|project-reports|cockpit|settings)/, cat: 'projekt' },
  { match: /^\/(projects|contacts)/, cat: 'agentur' },
  { match: /^\/(knowledge|factors)/, cat: 'wissen' },
  { match: /^\/(users|bookmarklet)/, cat: 'system' },
];
function categoryForPath(pathname) {
  if (pathname === '/') return 'projekt';
  return ROUTE_CATEGORY.find((r) => r.match.test(pathname))?.cat || '';
}

// ─── Compact Project Switcher (für Panel-Kontext, helles Theme) ─────────────
function PanelProjectSwitcher() {
  const [open, setOpen] = useState(false);
  const projects = useProjectStore((s) => s.projects);
  const currentProjectId = useProjectStore((s) => s.currentProjectId);
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);
  const navigate = useNavigate();
  const ref = useRef(null);

  const current = projects.find((p) => p.id === currentProjectId);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const initials = (current?.name || '?')
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div ref={ref} className="relative mx-2 mb-2.5">
      <div className="text-[10px] uppercase tracking-wider text-[#999] font-semibold mb-1.5 px-2">
        Aktuelles Projekt
      </div>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-[#F6F6F4] hover:bg-[#EFEFEC] border border-[#E9EBEC] transition-colors text-left"
      >
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold text-white shrink-0"
          style={{ backgroundColor: current?.color || '#0D3B50' }}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold text-[#1A1A1A] truncate leading-tight">
            {current?.name || 'Kein Projekt'}
          </div>
          {current?.domain && (
            <div className="text-[10px] text-[#999] truncate leading-tight mt-px">
              {current.domain}
            </div>
          )}
        </div>
        <span className="text-[#999] text-[10px]">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-black/10 overflow-hidden z-50 max-h-[420px] overflow-y-auto">
          <div className="px-3 py-2 border-b border-gray-100 text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
            Projekte ({projects.length})
          </div>
          {projects.map((p) => (
            <button
              key={p.id}
              onClick={() => {
                setCurrentProject(p.id);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition-colors text-left ${
                p.id === currentProjectId ? 'bg-[#0D3B50]/[.06]' : ''
              }`}
            >
              <div
                className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                style={{ backgroundColor: p.color || '#0D3B50' }}
              >
                {(p.name || '?')
                  .split(/\s+/)
                  .map((s) => s[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-medium text-gray-900 truncate">{p.name}</div>
                {p.domain && <div className="text-[10.5px] text-gray-400 truncate">{p.domain}</div>}
              </div>
              {p.id === currentProjectId && <span className="text-[#0D3B50] text-[12px]">✓</span>}
            </button>
          ))}
          <button
            onClick={() => {
              setOpen(false);
              navigate('/projects');
            }}
            className="w-full px-3 py-2.5 text-left text-[12px] text-[#C2185B] font-medium hover:bg-[#C2185B]/[.06] border-t border-gray-100"
          >
            + Projekte verwalten
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Rail-Button: direkter NavLink ──────────────────────────────────────────
function RailNavLink({ to, end, icon, label, railExpanded, onClick }) {
  return (
    <div className={`mb-1 group relative ${railExpanded ? 'px-3' : 'flex justify-center'}`}>
      <NavLink
        to={to}
        end={end}
        onClick={onClick}
        className={({ isActive }) =>
          `flex items-center h-10 rounded-lg transition-all ${
            railExpanded ? 'w-full px-2.5 gap-3 justify-start' : 'w-10 justify-center'
          } ${
            isActive
              ? 'bg-white/15 text-white'
              : 'text-white/65 hover:text-white hover:bg-white/10'
          }`
        }
      >
        <Icon path={icon} />
        {railExpanded && <span className="text-[13px] truncate">{label}</span>}
      </NavLink>
      {!railExpanded && (
        <div className="pointer-events-none absolute left-full ml-3 px-2 py-1 bg-[#1A1A1A] text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
          {label}
        </div>
      )}
    </div>
  );
}

// ─── Rail-Button: Toggle-Kategorie ──────────────────────────────────────────
function RailToggle({ category, icon, label, railExpanded, activeCategory, onClick, highlighted }) {
  const isOpen = activeCategory === category;
  return (
    <div className={`mb-1 group relative ${railExpanded ? 'px-3' : 'flex justify-center'}`}>
      <button
        onClick={onClick}
        className={`flex items-center h-10 rounded-lg transition-all ${
          railExpanded ? 'w-full px-2.5 gap-3 justify-start' : 'w-10 justify-center'
        } ${
          isOpen || highlighted
            ? 'bg-white/15 text-white'
            : 'text-white/65 hover:text-white hover:bg-white/10'
        }`}
      >
        <Icon path={icon} />
        {railExpanded && <span className="text-[13px] truncate flex-1 text-left">{label}</span>}
      </button>
      {!railExpanded && (
        <div className="pointer-events-none absolute left-full ml-3 px-2 py-1 bg-[#1A1A1A] text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
          {label}
        </div>
      )}
    </div>
  );
}

// ─── Sub-Item in Right Panel ────────────────────────────────────────────────
// Aufklappbare Gruppe im Projekt-Panel (z.B. Conversion, Reports)
function PanelGroup({ icon, label, children }) {
  const location = useLocation();
  const childActive = children.some((c) => c.to && location.pathname.startsWith(c.to));
  const [open, setOpen] = useState(childActive);
  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  return (
    <li>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-2.5 h-9 px-4 rounded-lg text-[13px] text-left transition-colors ${
          childActive ? 'text-[#0D3B50] font-medium' : 'text-[#444] hover:bg-[#0D3B50]/5'
        }`}
      >
        <span className={childActive ? 'text-[#0D3B50]' : 'text-[#888]'}>
          <Icon path={icon} />
        </span>
        <span className="truncate flex-1">{label}</span>
        <span className={`text-[#999] text-[9px] transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
      </button>
      {open && (
        <ul className="mt-0.5 ml-[26px] pl-3 border-l border-[#E9EBEC] flex flex-col gap-0.5">
          {children.map((c) => (
            <PanelItem key={c.to} {...c} />
          ))}
        </ul>
      )}
    </li>
  );
}

function PanelItem({ to, icon, label, end, onClick }) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        onClick={onClick}
        className={({ isActive }) =>
          `w-full flex items-center gap-2.5 h-9 px-4 rounded-lg text-[13px] text-left transition-colors ${
            isActive
              ? 'bg-[#0D3B50]/10 text-[#0D3B50] font-medium'
              : 'text-[#444] hover:bg-[#0D3B50]/5'
          }`
        }
      >
        {({ isActive }) => (
          <>
            <span className={isActive ? 'text-[#0D3B50]' : 'text-[#888]'}>
              <Icon path={icon} />
            </span>
            <span className="truncate flex-1">{label}</span>
          </>
        )}
      </NavLink>
    </li>
  );
}

// ─── Sidebar (Hauptkomponente) ──────────────────────────────────────────────
export default function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const appSettings = useAppSettings();
  const isLite = user?.cro_role === 'lite';
  const currentProjectId = useProjectStore((s) => s.currentProjectId);
  const { data: integrationsData } = useProjectIntegrations(isLite ? null : currentProjectId);

  const railExpanded = useLayoutStore((s) => s.railExpanded);
  const toggleRail = useLayoutStore((s) => s.toggleRail);
  const activeCategory = useLayoutStore((s) => s.activeCategory);
  const setActiveCategory = useLayoutStore((s) => s.setActiveCategory);
  const toggleCategory = useLayoutStore((s) => s.toggleCategory);
  const panelWidth = useLayoutStore((s) => s.panelWidth);
  const setPanelWidth = useLayoutStore((s) => s.setPanelWidth);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 1024);
  const [isResizing, setIsResizing] = useState(false);

  // Mobile-Detect
  useEffect(() => {
    const onResize = () => {
      const m = window.innerWidth < 1024;
      setIsMobile(m);
      if (!m) setMobileOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Resize-Drag fürs Panel
  const startResize = (e) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startW = panelWidth;
    const onMove = (ev) => {
      let w = startW + (ev.clientX - startX);
      if (w < 0) w = 0;
      if (w > 380) w = 380;
      setPanelWidth(w);
    };
    const onUp = () => {
      setIsResizing(false);
      // Final-Snapshot des aktuellen Werts wird im Store fortgeschrieben
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const bookedTypes = new Set(
    (integrationsData?.integrations || [])
      .filter((i) => i.is_booked)
      .map((i) => i.type)
  );

  const visibleProjectItems = PROJECT_NAV;

  const logoUrl = appSettings.logo_url || '';
  const companyName = appSettings.company_name || 'konversion.digital';
  const subtitle = appSettings.app_subtitle || 'CRO + SEO Platform';
  const railWidth = railExpanded ? 220 : 64;
  const effectivePanelWidth = activeCategory ? panelWidth : 0;

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // ignore
    }
    navigate('/login');
  };

  const routeCat = categoryForPath(location.pathname);

  // ─── Lite-Branch: simpel ───
  if (isLite) {
    return (
      <aside className="hidden lg:flex fixed top-2 left-2 bottom-2 z-40 bg-[#0D3B50] rounded-[15px] shadow-[0_8px_30px_rgba(9,9,9,0.10),0_2px_6px_rgba(9,9,9,0.06)] w-[220px] flex-col py-3 px-3">
        <button onClick={() => navigate('/')} className="flex items-center gap-2.5 mb-4 hover:opacity-80">
          {logoUrl ? (
            <img src={logoUrl} alt={companyName} className="w-10 h-10 rounded-lg object-contain" />
          ) : (
            <div className="w-10 h-10 bg-[#C2185B] rounded-lg flex items-center justify-center font-bold text-sm text-white">KD</div>
          )}
          <div className="min-w-0">
            <div className="font-semibold text-[13px] text-white truncate">{companyName}</div>
            <div className="text-[10.5px] text-white/45 truncate">{subtitle}</div>
          </div>
        </button>
        <NavLink
          to="/"
          end
          className={({ isActive }) =>
            `flex items-center gap-2.5 h-10 px-3 rounded-lg text-[13px] ${
              isActive ? 'bg-white/15 text-white' : 'text-white/65 hover:text-white hover:bg-white/10'
            }`
          }
        >
          <Icon path={I.analytics} />
          <span>Mein Tracking</span>
        </NavLink>
        <div className="flex-1" />
        <div className="border-t border-white/10 pt-3 flex items-center gap-2.5 px-1">
          <Avatar name={user?.name || 'User'} size={28} bg="rgba(255,255,255,0.18)" />
          <div className="flex-1 min-w-0">
            <div className="text-white text-[11.5px] font-medium truncate">{user?.name || 'User'}</div>
            <div className="text-white/45 text-[10px]">Lite</div>
          </div>
          <button onClick={handleLogout} className="text-white/45 hover:text-white p-1.5 rounded" title="Abmelden">
            <Icon path={I.logout} className="w-4 h-4" />
          </button>
        </div>
      </aside>
    );
  }

  // ─── Rail + Panel (Desktop) und Mobile-Drawer ───
  return (
    <>
      {/* MOBILE: Backdrop */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
        />
      )}

      {/* MOBILE: Drawer */}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-[#0D3B50] text-white flex flex-col transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <button
          onClick={() => {
            navigate('/');
            setMobileOpen(false);
          }}
          className="p-4 border-b border-white/10 flex items-center gap-3 text-left hover:bg-white/5"
        >
          {logoUrl ? (
            <img src={logoUrl} alt={companyName} className="w-10 h-10 rounded-lg object-contain shrink-0" />
          ) : (
            <div className="w-10 h-10 bg-[#C2185B] rounded-lg flex items-center justify-center font-bold text-sm shrink-0">KD</div>
          )}
          <div className="min-w-0">
            <div className="font-semibold text-base leading-tight truncate">{companyName}</div>
            <div className="text-sm text-white/50 truncate">{user?.name || ''}</div>
          </div>
        </button>
        <nav className="flex-1 p-3 overflow-y-auto space-y-1 text-[14px]">
          <MobileLink to="/" end icon={I.start} label="Start" onNav={() => setMobileOpen(false)} />

          {/* CTA: Projekt anlegen */}
          <button
            onClick={() => {
              navigate('/projects?new=1');
              setMobileOpen(false);
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 mt-1 rounded-lg bg-[#C2185B] text-white hover:bg-[#A91550]"
          >
            <Icon path={I.plus} className="w-5 h-5" />
            <span className="flex-1 text-left font-medium">Projekt anlegen</span>
          </button>

          <MobileSection title="Projekt">
            <PanelProjectSwitcherMobile onNav={() => setMobileOpen(false)} />
            {visibleProjectItems.map((it) =>
              it.children ? (
                <div key={it.label}>
                  <div className="px-3 pt-2 pb-0.5 text-[11px] uppercase tracking-wider text-white/35">{it.label}</div>
                  {it.children.map((c) => (
                    <MobileLink key={c.to} {...c} onNav={() => setMobileOpen(false)} />
                  ))}
                </div>
              ) : (
                <MobileLink key={it.to} {...it} onNav={() => setMobileOpen(false)} />
              )
            )}
            <MobileLink {...PROJECT_INTEGRATIONS_ITEM} onNav={() => setMobileOpen(false)} />
          </MobileSection>

          <MobileSection title="Funktionen">
            {FUNKTIONEN_ITEMS.map((it) => (
              <MobileLink key={it.to} {...it} onNav={() => setMobileOpen(false)} />
            ))}
          </MobileSection>

          <MobileSection title="Agent Center">
            {AGENT_CENTER_ITEMS.map((it) => (
              <MobileLink key={it.to} {...it} onNav={() => setMobileOpen(false)} />
            ))}
          </MobileSection>

          <MobileSection title="Agentur">
            {AGENTUR_ITEMS.map((it) => (
              <MobileLink key={it.to} {...it} onNav={() => setMobileOpen(false)} />
            ))}
          </MobileSection>

          <MobileSection title="Wissen">
            {WISSEN_ITEMS.map((it) => (
              <MobileLink key={it.to} {...it} onNav={() => setMobileOpen(false)} />
            ))}
          </MobileSection>

          <MobileSection title="System">
            {SYSTEM_ITEMS.map((it) => (
              <MobileLink key={it.to} {...it} onNav={() => setMobileOpen(false)} />
            ))}
          </MobileSection>
        </nav>
        <div className="p-3 border-t border-white/10 space-y-0.5">
          <a
            href="https://backoffice.konversion.digital"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/5"
          >
            <Icon path={I.external} />
            <span>Backoffice CRM</span>
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-white/55 hover:text-white hover:bg-white/5"
          >
            <Icon path={I.logout} />
            <span>Abmelden</span>
          </button>
        </div>
      </aside>

      {/* DESKTOP: Floating Frame */}
      <aside
        className={`hidden lg:flex fixed top-2 left-2 bottom-2 z-40 bg-white rounded-[15px] shadow-[0_8px_30px_rgba(9,9,9,0.10),0_2px_6px_rgba(9,9,9,0.06)] overflow-hidden ${
          !isResizing ? 'transition-[width] duration-200 ease-out' : ''
        } ${isResizing ? 'select-none cursor-ew-resize' : ''}`}
        style={{ width: railWidth + effectivePanelWidth }}
      >
        {/* ── Rail (dunkel) ── */}
        <div
          className="shrink-0 bg-[#0D3B50] flex flex-col py-3 transition-[width] duration-200 ease-out"
          style={{ width: railWidth }}
        >
          {/* Logo → Start */}
          <div className={`mb-3 group relative ${railExpanded ? 'px-3' : 'flex justify-center'}`}>
            <button
              onClick={() => {
                navigate('/');
                setActiveCategory('');
              }}
              className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
              title="Zum Start"
            >
              {logoUrl ? (
                <img src={logoUrl} alt={companyName} className="w-10 h-10 rounded-lg object-contain shrink-0" />
              ) : (
                <div className="w-10 h-10 bg-[#C2185B] rounded-lg flex items-center justify-center font-bold text-sm text-white shrink-0">
                  KD
                </div>
              )}
              {railExpanded && (
                <div className="min-w-0">
                  <div className="font-semibold text-[13px] text-white truncate leading-tight">{companyName}</div>
                  <div className="text-[10px] text-white/45 truncate leading-tight mt-px">{subtitle}</div>
                </div>
              )}
            </button>
            {!railExpanded && (
              <div className="pointer-events-none absolute left-full ml-3 px-2 py-1 bg-[#1A1A1A] text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                Start
              </div>
            )}
          </div>

          <div className="mx-3 mb-2 h-px bg-white/10" />

          {/* Start */}
          <RailNavLink
            to="/"
            end
            icon={I.start}
            label="Start"
            railExpanded={railExpanded}
            onClick={() => setActiveCategory('')}
          />

          {/* CTA: Projekt anlegen */}
          <div className={`mb-2 mt-0.5 group relative ${railExpanded ? 'px-3' : 'flex justify-center'}`}>
            <button
              onClick={() => {
                navigate('/projects?new=1');
                setActiveCategory('');
              }}
              className={`flex items-center h-10 rounded-lg bg-[#C2185B] text-white shadow-sm hover:bg-[#A91550] transition-all ${
                railExpanded ? 'w-full px-2.5 gap-3 justify-start' : 'w-10 justify-center'
              }`}
              title="Projekt anlegen"
            >
              <Icon path={I.plus} />
              {railExpanded && <span className="text-[13px] font-medium truncate">Projekt anlegen</span>}
            </button>
            {!railExpanded && (
              <div className="pointer-events-none absolute left-full ml-3 px-2 py-1 bg-[#1A1A1A] text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                Projekt anlegen
              </div>
            )}
          </div>

          {/* Toggle: Projekt */}
          <RailToggle
            category="projekt"
            icon={I.projekt}
            label="Projekt"
            railExpanded={railExpanded}
            activeCategory={activeCategory}
            highlighted={routeCat === 'projekt' && activeCategory === ''}
            onClick={() => toggleCategory('projekt')}
          />

          {/* Toggle: Funktionen */}
          <RailToggle
            category="funktionen"
            icon={I.funktionen}
            label="Funktionen"
            railExpanded={railExpanded}
            activeCategory={activeCategory}
            highlighted={false}
            onClick={() => toggleCategory('funktionen')}
          />

          {/* Toggle: Agent Center */}
          <RailToggle
            category="agent"
            icon={I.agentCenter}
            label="Agent Center"
            railExpanded={railExpanded}
            activeCategory={activeCategory}
            highlighted={routeCat === 'agent' && activeCategory === ''}
            onClick={() => toggleCategory('agent')}
          />

          {/* Toggle: Agentur */}
          <RailToggle
            category="agentur"
            icon={I.agentur}
            label="Agentur"
            railExpanded={railExpanded}
            activeCategory={activeCategory}
            highlighted={routeCat === 'agentur' && activeCategory === ''}
            onClick={() => toggleCategory('agentur')}
          />

          {/* Toggle: Wissen */}
          <RailToggle
            category="wissen"
            icon={I.wissen}
            label="Wissen"
            railExpanded={railExpanded}
            activeCategory={activeCategory}
            highlighted={routeCat === 'wissen' && activeCategory === ''}
            onClick={() => toggleCategory('wissen')}
          />

          <div className="flex-1" />

          {/* Rail-Expand-Toggle */}
          <div className={`mb-1 group relative ${railExpanded ? 'px-3' : 'flex justify-center'}`}>
            <button
              onClick={toggleRail}
              className={`flex items-center h-10 rounded-lg text-white/45 hover:text-white hover:bg-white/10 transition-all ${
                railExpanded ? 'w-full px-2.5 gap-3 justify-start' : 'w-10 justify-center'
              }`}
            >
              <svg
                className={`w-[18px] h-[18px] shrink-0 transition-transform duration-200 ${railExpanded ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                dangerouslySetInnerHTML={{ __html: I.chevrons }}
              />
              {railExpanded && <span className="text-[13px] truncate">Einklappen</span>}
            </button>
            {!railExpanded && (
              <div className="pointer-events-none absolute left-full ml-3 px-2 py-1 bg-[#1A1A1A] text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                Ausklappen
              </div>
            )}
          </div>

          {/* Toggle: System */}
          <RailToggle
            category="system"
            icon={I.system}
            label="System"
            railExpanded={railExpanded}
            activeCategory={activeCategory}
            highlighted={routeCat === 'system' && activeCategory === ''}
            onClick={() => toggleCategory('system')}
          />

          {/* CRM-Switch */}
          <div className={`mb-1 group relative ${railExpanded ? 'px-3' : 'flex justify-center'}`}>
            <a
              href="https://backoffice.konversion.digital"
              className={`flex items-center h-10 rounded-lg text-white/55 hover:text-white hover:bg-white/10 transition-all ${
                railExpanded ? 'w-full px-2.5 gap-3 justify-start' : 'w-10 justify-center'
              }`}
              title="Backoffice CRM öffnen"
            >
              <Icon path={I.external} />
              {railExpanded && <span className="text-[13px] truncate">Backoffice CRM</span>}
            </a>
            {!railExpanded && (
              <div className="pointer-events-none absolute left-full ml-3 px-2 py-1 bg-[#1A1A1A] text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                Backoffice CRM
              </div>
            )}
          </div>

          {/* Logout */}
          <div className={`group relative ${railExpanded ? 'px-3' : 'flex justify-center'}`}>
            <button
              onClick={handleLogout}
              className={`flex items-center h-10 rounded-lg text-white/55 hover:text-white hover:bg-white/10 transition-all ${
                railExpanded ? 'w-full px-2.5 gap-3 justify-start' : 'w-10 justify-center'
              }`}
              title="Abmelden"
            >
              <Icon path={I.logout} />
              {railExpanded && <span className="text-[13px] truncate">Abmelden</span>}
            </button>
            {!railExpanded && (
              <div className="pointer-events-none absolute left-full ml-3 px-2 py-1 bg-[#1A1A1A] text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                Abmelden
              </div>
            )}
          </div>
        </div>

        {/* ── Right Panel (hell, resizable) ── */}
        <div
          className={`relative h-full overflow-hidden ${
            !isResizing ? 'transition-[width] duration-200 ease-out' : ''
          }`}
          style={{ width: effectivePanelWidth }}
        >
          <div
            className={`h-full pt-5 px-2 overflow-y-auto transition-[opacity,filter] duration-300 ${
              effectivePanelWidth < 180 ? 'opacity-30 pointer-events-none' : ''
            }`}
          >
            {activeCategory === 'projekt' && (
              <>
                <h2 className="px-4 mb-3 text-[15px] font-semibold text-[#1A1A1A]">Projekt</h2>
                <div className="mx-2 mb-2.5">
                  <button
                    onClick={() => navigate('/projects?new=1')}
                    className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-[#C2185B] text-white hover:bg-[#A91550] transition-colors text-left"
                  >
                    <Icon path={I.plus} />
                    <span className="text-[13px] font-medium">Projekt anlegen</span>
                  </button>
                </div>
                <PanelProjectSwitcher />
                <ul className="flex flex-col gap-0.5">
                  {visibleProjectItems.map((it) =>
                    it.children
                      ? <PanelGroup key={it.label} {...it} />
                      : <PanelItem key={it.to} {...it} />
                  )}
                </ul>
                <div className="my-2 mx-4 border-t border-[#E9EBEC]" />
                <ul className="flex flex-col gap-0.5">
                  <PanelItem {...PROJECT_INTEGRATIONS_ITEM} />
                </ul>
              </>
            )}
            {activeCategory === 'funktionen' && (
              <>
                <h2 className="px-4 mb-3 text-[15px] font-semibold text-[#1A1A1A]">Funktionen</h2>
                <div className="px-4 mb-3 text-[11px] text-[#888] leading-snug">
                  Tätigkeitsorientierte Tools. SEO funktioniert auch ohne ausgewähltes Projekt.
                </div>
                <ul className="flex flex-col gap-0.5">
                  {FUNKTIONEN_ITEMS.map((it) => (
                    <PanelItem key={it.to} {...it} />
                  ))}
                </ul>
              </>
            )}
            {activeCategory === 'agent' && (
              <>
                <h2 className="px-4 mb-3 text-[15px] font-semibold text-[#1A1A1A]">Agent Center</h2>
                <ul className="flex flex-col gap-0.5">
                  {AGENT_CENTER_ITEMS.map((it) => (
                    <PanelItem key={it.to} {...it} />
                  ))}
                </ul>
              </>
            )}
            {activeCategory === 'agentur' && (
              <>
                <h2 className="px-4 mb-3 text-[15px] font-semibold text-[#1A1A1A]">Agentur</h2>
                <ul className="flex flex-col gap-0.5">
                  {AGENTUR_ITEMS.map((it) => (
                    <PanelItem key={it.to} {...it} />
                  ))}
                </ul>
              </>
            )}
            {activeCategory === 'wissen' && (
              <>
                <h2 className="px-4 mb-3 text-[15px] font-semibold text-[#1A1A1A]">Wissen</h2>
                <ul className="flex flex-col gap-0.5">
                  {WISSEN_ITEMS.map((it) => (
                    <PanelItem key={it.to} {...it} />
                  ))}
                </ul>
              </>
            )}
            {activeCategory === 'system' && (
              <>
                <h2 className="px-4 mb-3 text-[15px] font-semibold text-[#1A1A1A]">System</h2>
                <ul className="flex flex-col gap-0.5">
                  {SYSTEM_ITEMS.map((it) => (
                    <PanelItem key={it.to} {...it} />
                  ))}
                </ul>
              </>
            )}

            {activeCategory && (
              <div className="mt-4 mx-4 pt-3 border-t border-[#E9EBEC] text-[11px] text-[#999]">
                Eingeloggt als <span className="text-[#444] font-medium">{user?.name || 'User'}</span>
              </div>
            )}
          </div>

          {/* Resize Handle */}
          {activeCategory && (
            <div
              onMouseDown={startResize}
              className="absolute top-0 right-0 w-1.5 h-full cursor-ew-resize group z-10"
            >
              <div
                className={`absolute inset-y-0 right-0 w-px transition-colors ${
                  isResizing ? 'bg-[#0D3B50]/50' : 'bg-[#E9EBEC] group-hover:bg-[#0D3B50]/30'
                }`}
              />
            </div>
          )}
        </div>
      </aside>

      {/* MOBILE: Header (Burger) */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-[#E0E0DB] px-4 py-3 flex items-center gap-3"
        style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))' }}
      >
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-[#F6F6F4] transition-colors"
        >
          <Icon path={I.menu} className="w-6 h-6 text-[#1A1A1A]" />
        </button>
        <button onClick={() => navigate('/')} className="flex items-center gap-3 min-w-0">
          {logoUrl ? (
            <img src={logoUrl} alt={companyName} className="w-8 h-8 rounded-lg object-contain" />
          ) : (
            <div className="w-8 h-8 bg-[#C2185B] rounded-lg flex items-center justify-center font-bold text-xs text-white">
              KD
            </div>
          )}
          <span className="font-semibold text-[#1A1A1A] text-base truncate">{companyName}</span>
        </button>
      </div>
    </>
  );
}

// ─── Mobile-Helpers ─────────────────────────────────────────────────────────
function MobileSection({ title, children }) {
  return (
    <div className="pt-3 mt-3 border-t border-white/10">
      <div className="px-3 pb-1 text-[10px] uppercase tracking-wider text-white/35 font-semibold">{title}</div>
      {children}
    </div>
  );
}
function MobileLink({ to, end, icon, label, onNav }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNav}
      className={({ isActive }) =>
        `w-full flex items-center gap-3 px-3 py-2.5 rounded-lg ${
          isActive
            ? 'bg-white/15 text-white'
            : 'text-white/70 hover:text-white hover:bg-white/5'
        }`
      }
    >
      <Icon path={icon} className="w-5 h-5" />
      <span className="flex-1 text-left">{label}</span>
    </NavLink>
  );
}
function PanelProjectSwitcherMobile({ onNav }) {
  const projects = useProjectStore((s) => s.projects);
  const currentProjectId = useProjectStore((s) => s.currentProjectId);
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);
  const current = projects.find((p) => p.id === currentProjectId);
  return (
    <div className="px-3 py-2 mb-2 bg-white/5 rounded-lg">
      <div className="text-[10px] uppercase tracking-wider text-white/45 mb-1">Aktuelles Projekt</div>
      <select
        value={currentProjectId || ''}
        onChange={(e) => {
          setCurrentProject(Number(e.target.value));
          onNav?.();
        }}
        className="w-full bg-white/10 text-white text-[13px] rounded px-2 py-1.5 border border-white/10"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id} className="bg-[#0D3B50]">
            {p.name}
          </option>
        ))}
      </select>
    </div>
  );
}
